import {ReactionType} from "@dv5/design/icons/reactions/ReactionType"
import {ReactionWidgetData} from "@dv5/domain"
import {FC, MouseEvent, useEffect} from "react"
import {useWidgetEditor} from "../../hooks/useWidgetEditor"
import {EditorWidgetProps} from "../../model/WidgetProps"
import {widgetService} from "../../services/widgetService"
import {getReactionIcon} from "../common/ReactionPicker"
import {WidgetFrame} from "../common/WidgetFrame"
import {ReactionWidgetToolbar} from "./ReactionWidgetToolbar"
import './ReactionWidget.less'

type Props = EditorWidgetProps

export const ReactionWidget: FC<Props> = ({
                                              canvasId,
                                              widget,
                                              selected,
                                              settings,
                                              editingWidgetId,
                                              onStartDrag,
                                              onStartResize,
                                              onContextMenu,
                                              dragState,
                                              onStartLinkCreation,
                                              onCompleteLinkCreation,
                                              onNavigate,
                                              onDeleteDataLink,
                                              onStopEditing,
                                          }) => {
    const data = widget.data as ReactionWidgetData
    const styles = widgetService.getWidgetStyle(widget)
    const classes = widgetService.getWidgetClasses(widget, 'reactionWidget', selected, editingWidgetId, widget.commentsCount > 0)

    const {
        containerRef,
        resizeCursor,
        handlers,
        renderResizeHandles,
        renderLinkDots,
    } = useWidgetEditor({
        widget,
        selected,
        editingWidgetId,
        dragState,
        onStartDrag,
        onStartResize,
        onContextMenu,
        onStartLinkCreation,
        onCompleteLinkCreation,
        onNavigate,
        onDeleteDataLink,
    })

    useEffect(() => {
        if (editingWidgetId !== widget.id) {
            return
        }
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                onStopEditing()
            }
        }
        // @ts-expect-error
        document.addEventListener('mousedown', handleClickOutside)
        // @ts-expect-error
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [editingWidgetId, widget.id, onStopEditing])

    const renderReactionIcon = () => {
        const type = data.reactionType || ReactionType.Smile
        const ReactionIcon = getReactionIcon(type)
        if (!ReactionIcon) {
            return null
        }
        return (
            <div className='reaction-widget-icon'>
                <ReactionIcon width={Math.min(widget.width, widget.height) - 8} height={Math.min(widget.width, widget.height) - 8} />
            </div>
        )
    }

    if (editingWidgetId && editingWidgetId === widget.id) {
        const toolbarStyles = widgetService.getToolbarStyle(widget)
        return (
            <div className='reaction-widget-edit-container' ref={containerRef} onContextMenu={handlers.onContextMenu}>
                <ReactionWidgetToolbar
                    canvasId={canvasId}
                    widgetId={widget.id}
                    data={data}
                    toolbarStyles={toolbarStyles}
                    left={widget.left}
                    top={widget.top}
                    width={widget.width}
                    height={widget.height}
                />
                <div className={classes} style={styles}>
                    {renderReactionIcon()}
                </div>
            </div>
        )
    }

    return (
        <WidgetFrame
            baseClass='reactionWidget'
            canvasId={canvasId}
            widget={widget}
            settings={settings}
            selected={selected}
            editingWidgetId={editingWidgetId}
            resizeCursor={resizeCursor}
            handlers={handlers}
            containerRef={containerRef}
            renderResizeHandles={renderResizeHandles}
            renderLinkDots={renderLinkDots}
            onNavigate={onNavigate}
            onDeleteDataLink={onDeleteDataLink}
        >
            {renderReactionIcon()}
        </WidgetFrame>
    )
}
